import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';

export interface CSVExportOption {
  key: string;
  label: string;
  defaultChecked?: boolean;
  disabled?: boolean;
}

export interface CSVExportDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  options?: CSVExportOption[];
  onExport: (selectedOptions: string[], excludeFields: string[]) => Promise<void>;
  previewData?: {
    headers: Array<{ field: string; display: string }>;
    all_headers?: Array<{ field: string; display: string }>;
    personal_fields?: string[];
    rows: Array<Record<string, any>>;
    totalCount: number;
  };
  loading?: boolean;
  error?: string | null;
}

export const CSVExportDialog: React.FC<CSVExportDialogProps> = ({
  open,
  onClose,
  title,
  options = [],
  onExport,
  previewData,
  loading = false,
  error = null,
}) => {
  const [ selectedOptions, setSelectedOptions ] = useState<string[]>([]);
  const [ includePersonalFields, setIncludePersonalFields ] = useState<string[]>([]);
  const [ exporting, setExporting ] = useState(false);
  // 初期選択状態を設定
  useEffect(() => {
    const defaultSelected = options
    .filter(opt => opt.defaultChecked && !opt.disabled)
    .map(opt => opt.key);
    setSelectedOptions(defaultSelected);
  }, [ options ]);
  // 個人情報フィールドのヘッダー情報を取得
  const personalFieldHeaders = previewData?.all_headers?.filter(h =>
    previewData?.personal_fields?.includes(h.field)
  ) || [];
  const handleOptionChange = (key: string, checked: boolean) => {
    setSelectedOptions(prev =>
      checked
        ? [ ...prev, key ]
        : prev.filter(k => k !== key)
    );
  };
  const handlePersonalFieldChange = (field: string, checked: boolean) => {
    setIncludePersonalFields(prev =>
      checked
        ? [ ...prev, field ]
        : prev.filter(f => f !== field)
    );
  };
  const handleExport = async() => {
    setExporting(true);
    try {
      // 除外フィールドを計算（個人情報フィールドのうち、含めるものとして選択されなかったもの）
      const excludeFields = (previewData?.personal_fields || [])
      .filter(field => !includePersonalFields.includes(field));
      await onExport(selectedOptions, excludeFields);
      // ダウンロード成功時はダイアログを閉じる
      onClose();
    } catch (err) {
      // エラーは親コンポーネントで処理
    } finally {
      setExporting(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // backdropClickやescapeKeyでは閉じないようにする
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        onClose();
      }}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '80%',
          height: '60vh',
          maxWidth: '1400px',
          minHeight: '400px'
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h6">{title}</Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* 個人情報フィールドの選択 */}
        {personalFieldHeaders.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ color: 'warning.main' }}>
              個人情報の出力
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              以下の項目は個人情報です。必要な場合のみチェックを入れてください。
            </Alert>
            <FormGroup>
              {personalFieldHeaders.map(header => (
                <FormControlLabel
                  key={header.field}
                  control={
                    <Checkbox
                      checked={includePersonalFields.includes(header.field)}
                      onChange={(e) => handlePersonalFieldChange(header.field, e.target.checked)}
                      color="warning"
                    />
                  }
                  label={`${header.display}を含める`}
                />
              ))}
            </FormGroup>
          </Box>
        )}

        {/* オプション選択 */}
        {options.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              エクスポートオプション
            </Typography>
            <FormGroup>
              {options.map(option => (
                <FormControlLabel
                  key={option.key}
                  control={
                    <Checkbox
                      checked={selectedOptions.includes(option.key)}
                      onChange={(e) => handleOptionChange(option.key, e.target.checked)}
                      disabled={option.disabled}
                    />
                  }
                  label={option.label}
                />
              ))}
            </FormGroup>
          </Box>
        )}

        {/* プレビューデータ */}
        {previewData && (
          <Box>
            <Box sx={{ mb: 2, textAlign: 'center' }}>
              <Typography component="div">
                <Typography component="span" variant="h6" color="primary">
                  全 <Typography component="span" variant="h6" fontWeight="bold" color="primary">
                  {previewData.totalCount.toLocaleString()}
                </Typography> 件
                </Typography>
                {previewData.totalCount > 10 && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    （プレビュー: 先頭{previewData.rows.length}件）
                  </Typography>
                )}
              </Typography>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 450 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {/* 動的にヘッダーを表示（個人情報フィールドの選択状態に応じて） */}
                    {(previewData.all_headers || previewData.headers).map(header => {
                      // 個人情報フィールドかどうかをチェック
                      const isPersonalField = previewData.personal_fields?.includes(header.field);
                      // 個人情報フィールドの場合、選択されている場合のみ表示
                      if (isPersonalField && !includePersonalFields.includes(header.field)) {
                        return null;
                      }
                      return (
                        <TableCell key={header.field} sx={{ maxWidth: 200, minWidth: 80 }}>
                          <Typography variant="caption" fontWeight="bold">
                            {header.display}
                          </Typography>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.rows.map((row, index) => (
                    <TableRow key={index}>
                      {/* 動的にセルを表示（個人情報フィールドの選択状態に応じて） */}
                      {(previewData.all_headers || previewData.headers).map(header => {
                        const isPersonalField = previewData.personal_fields?.includes(header.field);
                        if (isPersonalField && !includePersonalFields.includes(header.field)) {
                          return null;
                        }
                        return (
                          <TableCell key={header.field} sx={{ maxWidth: 200, minWidth: 80 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                              title={row[header.field] != null ? String(row[header.field]) : undefined}
                            >
                              {row[header.field]}
                            </Typography>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* ローディング */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress/>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={exporting}>
          キャンセル
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          startIcon={exporting ? <CircularProgress size={20}/> : <DownloadIcon/>}
          disabled={exporting || loading}
        >
          {exporting ? 'ダウンロード中...' : 'ダウンロード'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
